import styled from '@emotion/styled/macro'
import React from 'react'

/** Served from web/public/images — root-relative per the app's asset convention. */
const HOCKEY_CANADA_LOGO = '/images/hockey-canada-logo.png'

/** Intrinsic size of the source asset, used to preserve its aspect ratio. */
const INTRINSIC_WIDTH = 1091
const INTRINSIC_HEIGHT = 1027

const ASPECT_RATIO = INTRINSIC_WIDTH / INTRINSIC_HEIGHT

interface HockeyCanadaLogoProps {
  /** Rendered width in px. Ignored when `height` is supplied. */
  width?: number
  /** Rendered height in px. Takes precedence — width is derived from it. */
  height?: number
  className?: string
}

/**
 * Hockey Canada logo.
 *
 * The mark is near-square (1091x1027), unlike the wide USA Hockey wordmark, so
 * callers laying it out beside that card should size it by `height` to match
 * visual weight. Whichever dimension is given, the other is derived so the
 * aspect ratio is always preserved.
 */
const HockeyCanadaLogo: React.FC<HockeyCanadaLogoProps> = ({
  width,
  height,
  className,
}) => {
  const resolvedHeight = height ?? (width ?? 180) / ASPECT_RATIO
  const resolvedWidth = height ? height * ASPECT_RATIO : width ?? 180

  return (
    <LogoImage
      src={HOCKEY_CANADA_LOGO}
      alt='Hockey Canada'
      width={Math.round(resolvedWidth)}
      height={Math.round(resolvedHeight)}
      className={className}
      $renderedWidth={resolvedWidth}
      $renderedHeight={resolvedHeight}
    />
  )
}

export default HockeyCanadaLogo

// ---- styled ----

const LogoImage = styled.img<{
  $renderedWidth: number
  $renderedHeight: number
}>`
  width: ${({ $renderedWidth }) => $renderedWidth}px;
  height: ${({ $renderedHeight }) => $renderedHeight}px;
  object-fit: contain;
  display: block;
`
